import { Trash2 } from 'lucide-react';
import { useLanguage } from '../../../features/language/useLanguage';
import type { Recipe } from '../../../types/recipe';
import type { RecipeDisplayEntry } from './types';

interface RecipeMobileListProps {
  entries: RecipeDisplayEntry[];
  canWrite: boolean;
  canDelete: boolean;
  deletingRecipeId: string | null;
  onRowClick: (recipe: Recipe) => void;
  onDelete: (recipe: Recipe) => void;
}

/** Mobile/tablet card list of recipes. */
export function RecipeMobileList({ entries, canWrite, canDelete, deletingRecipeId, onRowClick, onDelete }: RecipeMobileListProps) {
  const { t } = useLanguage();

  return (
    <div className="md:hidden divide-y divide-gray-200">
      {entries.map((entry) => {
        const recipe = entry.recipe;
        return (
          <div
            key={recipe.id}
            className={`p-4 space-y-3 bg-white hover:bg-gray-50 transition-colors ${canWrite ? 'cursor-pointer' : 'cursor-default'}`}
            onClick={() => onRowClick(recipe)}
          >
            <div className="flex justify-between items-start">
              <h3 className="text-sm font-medium text-gray-900">{recipe.name}</h3>
              <span className="text-sm font-medium text-primary-600">{recipe.targetBeanWeight} kg</span>
            </div>

            {recipe.description && <p className="text-sm text-gray-500 line-clamp-2">{recipe.description}</p>}

            <div className="flex justify-between items-center text-xs text-gray-400 pt-2">
              <div>
                <span className="font-medium text-gray-500">{t('recipes.form.shelfLifeDays')}: </span>
                {recipe.shelfLife ? t('recipes.page.shelfLifeDaysCount', { days: recipe.shelfLife }) : t('settingsMcp.values.none')}
              </div>
              <div className="flex items-center gap-3">
                <span className="text-primary-600 font-medium">{canWrite ? t('recipes.page.viewDetailsArrow') : t('common.view')}</span>
                {canDelete && (
                  <button
                    type="button"
                    data-test-id={`recipe-delete-button-mobile-${recipe.id}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      onDelete(recipe);
                    }}
                    disabled={deletingRecipeId === recipe.id}
                    className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                  >
                    <Trash2 className="h-4 w-4" />
                    {deletingRecipeId === recipe.id ? t('recipes.page.actions.deleting') : t('purchasing.supplierList.actions.delete')}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
